import React from "react";
import styled from "styled-components";
import { getAboutUsStorySection, getAboutUsStoryPara } from "../../api/axios";
import useSWR from "swr";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Styled Components
const OurStoryWrapper = styled.div`
  padding: 100px 0px 50px;
  max-width: 1536px;
  margin: 0 auto;
  padding-left: 20px;
  padding-right: 20px;
  transition: all 0.3s ease-out;

  @media (max-width: 1200px) {
    padding: 80px 20px 40px;
  }

  @media (max-width: 1024px) {
    text-align: center;
    padding: 60px 20px 30px;
  }

  @media (max-width: 768px) {
    padding: 40px 15px 20px;
  }

  @media (max-width: 480px) {
    padding: 30px 10px 15px;
  }

  @media (max-width: 360px) {
    padding: 25px 8px 10px;
  }
`;

const OurStoryHeading = styled.div`
  font-weight: bold;
  font-size: 50px;
  text-align: center;
  margin: 0;
  padding-bottom: 50px;
  color: ${props => props.theme?.palette?.text?.main || '#000000'};
  transition: all 0.3s ease-out;
  animation: fadeInUp 0.8s ease-out;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 1200px) {
    font-size: 45px;
    padding-bottom: 40px;
  }

  @media (max-width: 1024px) {
    font-size: 42px;
    padding-bottom: 30px;
  }

  @media (max-width: 768px) {
    font-size: 38px;
    padding-bottom: 25px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
    padding-bottom: 20px;
  }

  @media (max-width: 360px) {
    font-size: 28px;
    padding-bottom: 15px;
  }
`;

const StoryChangeColor = styled.span`
  color: ${props => props.theme?.palette?.primary?.main || '#FF8533'};
  text-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease-out;

  &:hover {
    text-shadow: 0px 6px 15px rgba(0, 0, 0, 0.25);
  }
`;

const OurStoryContent = styled.div`
  display: grid;
  grid-template-columns: 150px auto;
  margin-top: 20px;
  grid-gap: 50px;
  color: #000000;
  transition: all 0.3s ease-out;
  animation: fadeInUp 0.8s ease-out 0.2s both;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 1200px) {
    grid-gap: 40px;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(1, 1fr);
    align-items: center;
    padding: 10px 0px;
    grid-gap: 30px;
  }

  @media (max-width: 768px) {
    grid-gap: 25px;
    margin-top: 15px;
  }

  @media (max-width: 480px) {
    grid-gap: 20px;
    margin-top: 10px;
  }

  @media (max-width: 360px) {
    grid-gap: 15px;
    margin-top: 8px;
  }
`;

const StoryImg = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  transition: all 0.3s ease-out;

  @media (max-width: 1024px) {
    margin-top: 0;
    padding-top: 0;
    gap: 20px;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  @media (max-width: 768px) {
    gap: 26px;
  }

  @media (max-width: 480px) {
    gap: 21px;
  }
`;

const MemberImage = styled.img`
  border-radius: 30px;
  width: 150px;
  height: 150px;
  object-fit: cover;
  transition: all 0.3s ease-out;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0px 8px 20px rgba(0, 0, 0, 0.25);
  }

  @media (max-width: 1200px) {
    width: 140px;
    height: 140px;
  }

  @media (max-width: 1024px) {
    width: 153px;
    height: 153px;
  }

  @media (max-width: 768px) {
    width: 141px;
    height: 141px;
  }
  @media (max-width: 360px) {
    width: 110px;
    height: 110px;
  }
`;

const MemberInfo = styled.p`
  text-align: center;
  color: #000000;
  font-weight: 400;
  font-size: 18px;
  line-height: 30px;
  letter-spacing: 0.039em;
  margin-top: 10px;
  transition: all 0.3s ease-out;

  @media (max-width: 1200px) {
    font-size: 17px;
    line-height: 28px;
  }

  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 26px;
  }

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 24px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 22px;
  }

  @media (max-width: 360px) {
    font-size: 13px;
    line-height: 20px;
  }
`;

const StoryText = styled.div`
  color: #000000;
  font-weight: 550;
  font-size: 23px;
  text-align: justify;
  line-height: 38px;
  letter-spacing: -0.011em;
  word-spacing: 0.05em;
  transition: all 0.3s ease-out;
  animation: fadeInUp 0.8s ease-out 0.4s both;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  p {
    margin: 0 0 20px 0;
    transition: all 0.3s ease-out;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  @media (max-width: 1200px) {
    font-size: 22px;
    line-height: 36px;
  }

  @media (max-width: 1024px) {
    font-size: 20px;
    line-height: 34px;
    text-align: center;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 30px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 28px;
  }

  @media (max-width: 360px) {
    font-size: 15px;
    line-height: 26px;
  }
`;

function OurStory() {
  const theme = useTheme();

  const { data: storyData } = useSWR(
    `aboutUsStorySection`,
    async () => {
      try {
        const { data } = await getAboutUsStorySection();
        return data?.data?.[0] || [];
      } catch (error) {
        console.error("error: ", error);
      }
    },
    {
      revalidateOnFocus: false,
    }
  );

  const { data: paragraph } = useSWR(
    `aboutUsStoryParagraphs`,
    async () => {
      try {
        const { data } = await getAboutUsStoryPara();
        return data?.data?.[0] || [];
      } catch (error) {
        console.error("error: ", error);
      }
    },
    {
      revalidateOnFocus: false,
    }
  );

  return (
    <OurStoryWrapper>
      <OurStoryHeading theme={theme}>
        Our<StoryChangeColor theme={theme}> Story</StoryChangeColor>
      </OurStoryHeading>
      <OurStoryContent>
        <StoryImg>
            <Box component='div'>
                <Box component="div">
                <MemberImage
                  src={storyData?.image1}
                  alt="Team Member 1"
                />
                </Box>
                 <Box component="div">
                <MemberInfo>
                  {storyData?.name1}
                  <br />
                  {storyData?.position1}
                </MemberInfo>
                </Box>
            </Box>
            <Box component='div'>
                <MemberImage
                src={storyData?.image2}
                alt="Team Member 2"
              />
              <MemberInfo>
                {storyData?.name2}
                <br />
                {storyData?.position2}
              </MemberInfo>
            </Box>
        </StoryImg>
        <StoryText>
          <p>{paragraph?.paragraph1}</p>
          <p>{paragraph?.paragraph2}</p>
          <p>{paragraph?.paragraph3}</p>
        </StoryText>
      </OurStoryContent>
    </OurStoryWrapper>
  );
}

export default OurStory;
